import { Grid, TextField, Typography, createStyles, makeStyles } from "@material-ui/core";
import React from "react";
import { WithIntlProps, injectIntl } from "react-intl";
import { RequiredCandidateMyProfile, Switzerland } from "../../../data/constants";
import { CandidateViewModel } from "../../../model/CandidateViewModel";
import { GetTranslation } from "../../../translations/GetTranslation";
import { TranslationKey, isTranslationKey } from "../../../translations/TranslationKey";
import { getValidDate } from "../../../utils/Dates";
import FormGridCell from "../../common/FormGridCell";
import { CompanySelect } from "../../common/customAutocomplete/CompanySelect";
import { CountrySelect } from "../../common/customAutocomplete/CountrySelect";
import { GenderSelect } from "../../common/customAutocomplete/GenderSelect";
import { IPlaceOfOriginOption, PlaceOfOriginSelect } from "../../common/customAutocomplete/PlaceOfOriginSelect";
import { OptionType } from "../../common/customSelect/CustomSelect";
import DatePicker from "../../common/datePicker/DatePicker";
import AhvNoTextField from "../../common/textField/AhvNoTextField";
import CiceroNoTextField from "../../common/textField/CiceroNoTextField";
import EmailTextField from "../../common/textField/EmailTextField";
import PhoneNoTextField from "../../common/textField/PhoneNoTextField";
import TranslatedMessage from "../../translations/TranslatedMessage";

type CandidateFieldsProps = {
  candidate: CandidateViewModel;
  isRequired: RequiredCandidateMyProfile;
  places: OptionType[];
  onPropertyValueChanges?: (part: Partial<CandidateViewModel>) => void;
  onChangeAhvIsValid?: (isValid: boolean) => void;
  onChangeCiceroIsValid?: (isValid: boolean) => void;
  onChangePhoneIsValid?: (isValid: boolean) => void;
  onChangeMobileIsValid?: (isValid: boolean) => void;
  onChangeEmailIsValid?: (isValid: boolean) => void;
} & WithIntlProps<any>;

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      marginTop: "40px",
    },
  })
);

const CandidateFields: React.FunctionComponent<CandidateFieldsProps> = ({
  candidate,
  intl,
  isRequired,
  countries,
  places,
  ...handlers
}) => {
  const classes = useStyles({});

  const handleTextChange =
    (key: keyof CandidateViewModel) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleChange(key, event.target.value);

  const handleNationalityChange = (value?: string) => {
    if (value === Switzerland) {
      handleChange("placeOfOrigin", "");
      handleChange("nationalityIsSwiss", true);
    } else {
      handleChange("canton", "");
      handleChange("nationalityIsSwiss", false);
      handleChange("placeOfOrigin", value);
    }
    handleChange("nationality", value);
  };

  const handleCompanyChanged = (value?: string) => handleChange("employerInformation", value);

  const handlePlaceOfOriginChange = (option: IPlaceOfOriginOption | null) => {
    handleChange("placeOfOrigin", option?.value);
    handleChange("canton", option?.canton);
  };

  const handleDateChange = (key: keyof CandidateViewModel) => (date: Date | null) => {
    handleChange(key, date);
  };
  const handleChange = (
    key: keyof CandidateViewModel,
    value: string | string[] | Date | boolean | null | undefined
  ) => {
    let part: Partial<CandidateViewModel> = {
      [key]: value,
    };

    if (handlers.onPropertyValueChanges) {
      handlers.onPropertyValueChanges(part);
    }
  };

  // For the transition from long name to short names of candidate's cantons.
  const getCantonDisplayValue = (): string => {
    if (!candidate.canton) {
      return "";
    }
    const key = `canton.${candidate.canton.toLowerCase()}`;
    if (isTranslationKey(key)) {
      return GetTranslation(intl, key as TranslationKey);
    }
    return candidate.canton;
  };

  return (
    <>
      <Typography variant="h6">
        <TranslatedMessage id="person.section.personalDetails" />
      </Typography>
      <Grid container={true} spacing={4}>
        <FormGridCell size="third">
          <TextField
            fullWidth={true}
            error={isRequired.isFirstnameEmpty}
            required
            label={GetTranslation(intl, "person.field.firstName")}
            onChange={handleTextChange("firstName")}
            value={candidate.firstName || ""}
          />
        </FormGridCell>
        <FormGridCell size="third">
          <TextField
            fullWidth={true}
            error={isRequired.isLastnameEmpty}
            required
            label={GetTranslation(intl, "person.field.lastName")}
            onChange={handleTextChange("lastName")}
            value={candidate.lastName || ""}
          />
        </FormGridCell>
        <GenderSelect
          xs={12}
          sm={6}
          md={4}
          error={isRequired.isGenderEmpty}
          required
          value={candidate.gender}
          onChange={(value) => handleChange("gender", value)}
        />
        <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
          <DatePicker
            label={<TranslatedMessage id="person.field.birthDate" />}
            value={getValidDate(candidate.birthdate)}
            onChange={handleDateChange("birthdate")}
            required={true}
            error={isRequired.isBirthDateEmpty}
            disabled={candidate.disablePersonalFields}
          />
        </Grid>
        <FormGridCell size="third">
          <AhvNoTextField
            error={isRequired.isAhvNoEmpty}
            required
            label={<TranslatedMessage id="person.field.ahvNumber" />}
            onChangeIsValid={handlers.onChangeAhvIsValid}
            onChange={handleTextChange("ahvNumber")}
            value={candidate.ahvNumber || ""}
            disabled={candidate.disablePersonalFields}
          />
        </FormGridCell>
        <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
          <EmailTextField
            error={isRequired.isEmailEmpty}
            required
            label={"Email"}
            onChangeIsValid={handlers.onChangeEmailIsValid}
            onChange={handleTextChange("emailAddress")}
            value={candidate.emailAddress || ""}
          />
        </Grid>
        <FormGridCell size="third">
          <PhoneNoTextField
            label={GetTranslation(intl, "person.field.phoneNumber")}
            onChangeIsValid={handlers.onChangePhoneIsValid}
            onChange={handleTextChange("phoneNumber")}
            value={candidate.phoneNumber || ""}
          />
        </FormGridCell>
        <FormGridCell size="third">
          <PhoneNoTextField
            label={GetTranslation(intl, "person.field.mobileNumber")}
            onChangeIsValid={handlers.onChangeMobileIsValid}
            onChange={handleTextChange("mobileNumber")}
            value={candidate.mobileNumber || ""}
          />
        </FormGridCell>
        <FormGridCell size="third">
          <CiceroNoTextField
            label={<TranslatedMessage id="person.field.ciceroNumber" />}
            onChangeIsValid={handlers.onChangeCiceroIsValid}
            onChange={handleTextChange("ciceroNumber")}
            value={candidate.ciceroNumber || ""}
          />
        </FormGridCell>
        <CountrySelect
          xs={12}
          sm={6}
          md={4}
          label={GetTranslation(intl, "person.field.homeCountry")}
          defaultValue={Switzerland}
          value={candidate.nationalityIsSwiss ? Switzerland : candidate.placeOfOrigin}
          onChange={handleNationalityChange}
          required
          error={isRequired.isNationalityEmpty}
          disabled={candidate.disablePersonalFields}
        />
        {candidate.nationalityIsSwiss ? (
          <React.Fragment>
            <PlaceOfOriginSelect
              xs={12}
              sm={6}
              md={4}
              value={candidate.placeOfOrigin}
              onChange={(value) => handlePlaceOfOriginChange(value)}
              required
              error={isRequired.isPlaceOfOriginEmpty}
              disabled={candidate.disablePersonalFields}
            />
            <FormGridCell size="third">
              <TextField
                error={isRequired.isCantonEmpty}
                required
                fullWidth={true}
                inputProps={{ readOnly: true }}
                label={GetTranslation(intl, "candidate.field.canton")}
                value={getCantonDisplayValue()}
              />
            </FormGridCell>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <FormGridCell size="third"></FormGridCell>
            <FormGridCell size="third"></FormGridCell>
          </React.Fragment>
        )}
      </Grid>
      <Typography variant="h6" className={classes.header}>
        <TranslatedMessage id="person.section.businessData" />
      </Typography>
      <Grid container={true} spacing={4}>
        <CompanySelect
          xs={12}
          sm={6}
          md={4}
          label={GetTranslation(intl, "person.field.employer")}
          value={candidate.employerInformation}
          onChange={handleCompanyChanged}
          required={true}
          error={isRequired.isEmployerInformationEmpty}
        />
      </Grid>
    </>
  );
};

CandidateFields.defaultProps = {
  onPropertyValueChanges: () => {},
  onChangeAhvIsValid: () => {},
  onChangePhoneIsValid: () => {},
  onChangeMobileIsValid: () => {},
  onChangeEmailIsValid: () => {},
} as Partial<CandidateFieldsProps>;

export default injectIntl(CandidateFields);
